import { useState } from 'react'
import filter from 'lodash/filter'
import isEmpty from 'lodash/isEmpty'
import join from 'lodash/join'
import map from 'lodash/map'
import { Dropdown, Modal as ModalCore } from 'react-bootstrap'
import type { File } from '@ztechapps/common.contract'
import { htmlToPlainText } from '@ztechapps/common.utils'
import { SaveCancelButtons } from '@ztechapps/webui.button'
import { RichTextInput, RichTextInputVariantEnum } from '@ztechapps/webui.input'
import { Modal, ModalDragHandle } from '@ztechapps/webui.modal'
import { Alert, HorizontalStack, NullComponent, ShowIf, TooltipPopOver } from '@ztechapps/webui.util'
import { useConversationInboxContext } from './Context'
import { appendMessageLine } from '../Channel/appendMessageLine'
import { StagedAttachments } from '../Chat/StagedAttachments'
import { composerStatusBarInit } from './constants'
import { ModalComposerToolbar } from './ModalComposerToolbar'
import { summarizeBatchRecipients } from './summarizeBatchRecipients'
import { useConversationBatchActions } from './useConversationBatchActions'
import { useFormalizeMessage } from '../Channel/useFormalizeMessage'
import styles from './BatchReplyModal.module.scss'

export type BatchReplyModalProps = {
  disabled?: boolean
  title?: string
}

/**
 * The recipient summary above the composer is the confirmation — it names the operation, the
 * message count and the distinct-recipient count, and Cancel writes nothing — so this is
 * deliberately not wrapped in `useConfirmDialog` as well.
 */
export const BatchReplyModal = ({ disabled, title }: BatchReplyModalProps) => {
  const { chatListWithSelection } = useConversationInboxContext()
  const { selectedList } = chatListWithSelection
  const [show, setShow] = useState(false)
  const [value, setValue] = useState('')
  const [attachments, setAttachments] = useState<File[]>([])
  const { replyToConversations, replying } = useConversationBatchActions()
  const { formalizing, onFormalize } = useFormalizeMessage()
  // Send and formalize lock each other out — one flag, computed here since the modal has no
  // ChannelProvider `status.busy` to read.
  const busy = replying || formalizing

  const { addresses, distinctAddresses, duplicateAddresses, noSenderCount } = summarizeBatchRecipients(selectedList)

  const handleClose = () => {
    setValue('')
    setAttachments([])
    setShow(false)
  }

  const handleSend = async () => {
    await replyToConversations(map(selectedList, (chat) => ({ chatId: chat._id, content: value, attachments })))
    handleClose()
  }

  // Functional update so an attach completion landing after keystrokes appends below the
  // latest staged text — a render-time snapshot would overwrite it. The pick still lands
  // on its own line below whatever is staged.
  const insertMessage = (text: string) => setValue((prev) => appendMessageLine(prev, text))

  // Every selected conversation lacking a sender leaves nothing sendable — same "no
  // address" condition the warning banner above already names via `noSenderCount`.
  const sendDisabled = isEmpty(htmlToPlainText(value).trim()) || isEmpty(addresses)

  return (
    <>
      {/* A disabled item takes no pointer events, so the wrapper div carries the hover. */}
      <TooltipPopOver tooltipBody={title}>
        <div>
          <Dropdown.Item onClick={() => setShow(true)} disabled={disabled}>
            Reply
          </Dropdown.Item>
        </div>
      </TooltipPopOver>
      <Modal show={show} onHide={busy ? undefined : handleClose} size="lg" trigger={NullComponent}>
        <ModalDragHandle closeButton={!busy}>Reply to selected conversations</ModalDragHandle>
        <ModalCore.Body>
          <Alert type="info">
            <b>{addresses.length}</b> message(s) will be sent to <b>{distinctAddresses.length}</b> distinct address(es).
            One message per conversation, so every conversation thread records its own reply.
          </Alert>
          <ShowIf condition={noSenderCount > 0}>
            <Alert type="warning">
              <b>{noSenderCount}</b> selected conversation(s) have no sender address on file and will fail to send.
            </Alert>
          </ShowIf>
          <ShowIf condition={!isEmpty(duplicateAddresses)}>
            <Alert type="warning">
              This address will receive more than one message:{' '}
              <span className="text-break">{join(duplicateAddresses, ', ')}</span>
            </Alert>
          </ShowIf>
          <div className="mb-3">
            <label className="form-label fw-semibold">Recipients</label>
            <div className="border rounded overflow-auto" style={{ maxHeight: 150 }}>
              {map(selectedList, (chat, index) => (
                <HorizontalStack
                  key={chat._id}
                  gap={2}
                  className={`justify-content-between px-2 py-1 ${index < selectedList.length - 1 ? 'border-bottom' : ''}`}
                >
                  <span className={`text-truncate ${styles.flexTruncate}`}>{chat.sender?.profile?.email || '—'}</span>
                  <span className="text-muted small text-nowrap flex-shrink-0">{chat.code}</span>
                </HorizontalStack>
              ))}
            </div>
          </div>
          <RichTextInput
            value={value}
            onChange={setValue}
            disabled={busy}
            variant={RichTextInputVariantEnum.Compact}
            height="30vh"
            init={composerStatusBarInit}
            toolbarEnd={
              <ModalComposerToolbar
                message={value}
                setMessage={setValue}
                insertMessage={insertMessage}
                attachments={attachments}
                onAttach={(files) => setAttachments((prev) => [...prev, ...files])}
                disabled={busy}
                formalizing={formalizing}
                onFormalize={onFormalize}
              />
            }
          />
          <StagedAttachments
            attachments={attachments}
            onRemove={(fileId) => setAttachments((prev) => filter(prev, (file) => file._id !== fileId))}
            disabled={busy}
          />
        </ModalCore.Body>
        <ModalCore.Footer>
          <HorizontalStack gap={2} className="justify-content-end">
            <SaveCancelButtons
              onSave={handleSend}
              onCancel={handleClose}
              disabled={sendDisabled || busy}
              isBusy={replying}
              saveIcon="paper-plane"
              saveBusyText=" "
              // Count the sendable conversations — those with a resolvable address — the
              // same count the info banner above already names, not every selected row.
              saveText={`Send ${addresses.length} message(s)`}
              saveTitle={sendDisabled ? 'Type a message to send' : `Send ${addresses.length} message(s)`}
              cancelText="Cancel"
              cancelTitle="Close without sending"
            />
          </HorizontalStack>
        </ModalCore.Footer>
      </Modal>
    </>
  )
}
